import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';
import { Head } from 'vite-react-ssg';

// Services
import { getFooter, type FooterType } from '@/services/footer/footerService';
import { getHeader, type HeaderType } from '@/services/header/headerService';
import { getNewsPage, type NewsPageType } from '@/services/news-page/newsPageService';
import { getAllNews } from '@/services/news/newsService';

// Components
import Header from '@/components/organisms/Header/Header';
import Footer from '@/components/organisms/Footer/Footer';
import { PageBlocks } from '@/components/blocks/Blocks';
import PreTitle from '@/components/atoms/PreTitle/PreTitle';
import Heading from '@/components/atoms/Heading/Heading';
import BlocMiseEnAvant from '@/components/blocks/BlocMiseEnAvant/BlocMiseEnAvant';

// Utils
import { resources } from '@/locales/resources';
import { buildLocalizedPath, getLocale } from '@/utils/localeSlug';
import { buildSEOUrl } from '@/utils/seo';
import { withTrailingSlashIfInternal } from '@/utils/trailingSlash';

// Styles
import styles from './News.module.css';

type AppLocale = keyof typeof resources;

const NEWS_PAGE_SUFFIX = /\/page\/(\d+)\/?$/;

export async function newsPageLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const locale = getLocale(url);
  const currentPage = Number(url.pathname.match(NEWS_PAGE_SUFFIX)?.[1] ?? 1);

  const [header, footer, newsPage, news] = await Promise.all([
    getHeader(locale),
    getFooter(locale),
    getNewsPage(locale),
    getAllNews(locale, currentPage),
  ]);

  return {
    header,
    footer,
    newsPage,
    news,
    currentPage,
    locale,
  };
}

function News() {
  const baseUrl = import.meta.env.VITE_DOMAIN;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const data = useLoaderData() as Awaited<ReturnType<typeof newsPageLoader>> | null;

  const legacyPage = Number(searchParams.get('page'));
  const currentPage = data?.currentPage ?? 1;
  const locale = (data?.locale ?? 'fr-FR') as AppLocale;
  const totalPages = Math.max(1, data?.news.meta.pagination.pageCount ?? 1);
  const redirectNewsPage = data?.newsPage;

  const pageSlugFor = (slug: string, page: number) => (
    page === 1 ? slug : `${slug}/page/${page}`
  );
  const pagePathFor = (slug: string, page: number) => (
    buildLocalizedPath(pageSlugFor(slug, page), locale)
  );

  useEffect(() => {
    if (legacyPage > 1 && currentPage === 1 && redirectNewsPage) {
      navigate(
        pagePathFor(redirectNewsPage.slug, Math.min(legacyPage, totalPages)),
        { replace: true },
      );
    }
  }, [legacyPage, currentPage, totalPages, redirectNewsPage, navigate, locale]);

  if (!data?.newsPage) return null;

  const { header, footer, newsPage, news } = data;

  const pageSlug = (page: number) => pageSlugFor(newsPage.slug, page);
  const pagePath = (page: number) => pagePathFor(newsPage.slug, page);

  const metaTitle = currentPage === 1
    ? newsPage.seo.metaTitle
    : `${newsPage.seo.metaTitle} ${t('news-page.titlePageSuffix', { page: currentPage })}`;

  const canonicalHref = newsPage.seo.canonicalLink
    ? withTrailingSlashIfInternal(newsPage.seo.canonicalLink, baseUrl)
    : buildSEOUrl(baseUrl, newsPage.locale, pageSlug(currentPage));

  return (
    <main>
      <Head htmlAttributes={{ lang: newsPage.locale }}>
        <title>{metaTitle}</title>
        <meta name="description" content={newsPage.seo.metaDescription} />
        <link rel="canonical" href={canonicalHref} />
        {currentPage === 1 ? (
          <>
            <link
              rel="alternate"
              hrefLang={newsPage.locale}
              href={buildSEOUrl(baseUrl, newsPage.locale, newsPage.slug)}
            />
            {newsPage.localizations.map((localization) => (
              <link
                key={localization.documentId}
                rel="alternate"
                hrefLang={localization.locale}
                href={buildSEOUrl(baseUrl, localization.locale, localization?.slug)}
              />
            ))}
            {newsPage.locale === 'fr-FR' ? (
              <link
                rel="alternate"
                hrefLang="x-default"
                href={buildSEOUrl(baseUrl, newsPage.locale, newsPage.slug)}
              />
            ) : (
              (() => {
                const fr = newsPage.localizations.find((loc) => loc.locale === 'fr-FR');
                if (fr) {
                  return (
                    <link
                      rel="alternate"
                      hrefLang="x-default"
                      href={buildSEOUrl(baseUrl, fr.locale, fr.slug)}
                    />
                  );
                }
                return null;
              })()
            )}
          </>
        ) : null}
      </Head>

      <Header
        color="dark"
        header={header as HeaderType}
        localizations={newsPage.localizations}
      />

      <div className={styles.body}>
        <PageBlocks blocks={(newsPage as NewsPageType).blocks} />
        <div className={styles.container}>
          <div className={styles['news-body']}>
            <div className={styles.content}>
              <PreTitle
                preTitle={newsPage.preTitle}
                color="dark"
              />
              <Heading
                title={newsPage.title}
                size="h2"
                Tag="h2"
                color="dark"
                align="center"
              />
            </div>
          </div>
          <div className={styles.cards}>
            {news.data.map((item) => (
              <BlocMiseEnAvant
                key={item.documentId}
                image={item.image}
                title={item.title}
                description={item.description}
                url={item.url}
                publicationDate={item.publicationDate}
                imagePosition={item.imagePosition}
              />
            ))}
          </div>
          <div className={styles.pagination}>
            {currentPage <= 1 ? (
              <span className={styles['pagination-button']}>{t('news-page.previous')}</span>
            ) : (
              <Link to={pagePath(currentPage - 1)} className={styles['pagination-button']}>
                {t('news-page.previous')}
              </Link>
            )}
            <div className={styles['pagination-pages']}>
              {currentPage > 1 ? (
                <Link to={pagePath(currentPage - 1)} className={styles['pagination-button']}>
                  {currentPage - 1}
                </Link>
              ) : null}
              <div className={styles['pagination-active']}>
                {currentPage}
              </div>
              {currentPage < totalPages ? (
                <Link to={pagePath(currentPage + 1)} className={styles['pagination-button']}>
                  {currentPage + 1}
                </Link>
              ) : null}
            </div>
            {currentPage >= totalPages ? (
              <span className={styles['pagination-button']}>{t('news-page.next')}</span>
            ) : (
              <Link to={pagePath(currentPage + 1)} className={styles['pagination-button']}>
                {t('news-page.next')}
              </Link>
            )}
          </div>
        </div>
      </div>
      <Footer
        footer={footer as FooterType}
      />
    </main>
  );
}

export default News;
